import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Plus, Trash2, Repeat, TrendingUp } from 'lucide-react'
import { accountService, incomeService } from '../lib/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { formatRupees } from '../utils/currency'

const IncomesPage = () => {
  const [loading, setLoading] = useState(true)
  const [incomes, setIncomes] = useState([])
  const [stats, setStats] = useState(null)
  const [accounts, setAccounts] = useState([])
  const [showAddModal, setShowAddModal] = useState(false)

  const [formData, setFormData] = useState({
    title: '',
    amount: '',
    category: 'Salary',
    source: '',
    notes: '',
    account_id: '',
    is_recurring: false,
    recurring_frequency: 'monthly',
  })

  const accountMap = useMemo(() => {
    return accounts.reduce((acc, item) => {
      acc[item.id] = item
      return acc
    }, {})
  }, [accounts])

  const loadData = async () => {
    try {
      setLoading(true)
      const [incomeData, incomeStats, accountData] = await Promise.all([
        incomeService.getIncomes(),
        incomeService.getStats(),
        accountService.getAccounts(),
      ])

      setIncomes(incomeData.incomes || [])
      setStats(incomeStats || null)
      setAccounts(accountData.accounts || [])
    } catch (error) {
      toast.error(error.message || 'Failed to load incomes')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const handleCreateIncome = async (e) => {
    e.preventDefault()

    try {
      await incomeService.createIncome({
        ...formData,
        amount: Number(formData.amount),
        account_id: formData.account_id || null,
      })

      toast.success('Income added successfully')
      setShowAddModal(false)
      setFormData({
        title: '',
        amount: '',
        category: 'Salary',
        source: '',
        notes: '',
        account_id: '',
        is_recurring: false,
        recurring_frequency: 'monthly',
      })
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to add income')
    }
  }

  const handleDeleteIncome = async (incomeId) => {
    if (!window.confirm('Delete this income record?')) {
      return
    }

    try {
      await incomeService.deleteIncome(incomeId)
      toast.success('Income deleted successfully')
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to delete income')
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading incomes..." />
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Income Management</h1>
          <p className="text-gray-600 dark:text-gray-400">Track salary, side income, and recurring inflows.</p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Income
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Recorded Income</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatRupees(stats?.total_income || 0)}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Current Month Income</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatRupees(stats?.monthly_total || 0)}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Recurring Projection (Monthly)</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatRupees(stats?.recurring_monthly_projection || 0)}</p>
        </div>
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Income History</h2>
        {incomes.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">No income records available yet.</p>
        ) : (
          <div className="space-y-3">
            {incomes.map((income) => (
              <div
                key={income.id}
                className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700 flex items-center justify-between"
              >
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">{income.title}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {income.category} • {income.source || 'General'} • {new Date(income.created_at).toLocaleDateString()}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    {income.is_recurring && (
                      <span className="text-xs px-2 py-1 rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200 inline-flex items-center gap-1">
                        <Repeat className="h-3 w-3" />
                        Recurs {income.recurring_frequency || 'monthly'}
                      </span>
                    )}
                    {income.account_id && (
                      <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                        Account: {accountMap[income.account_id]?.name || 'Linked'}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="font-bold text-emerald-600 dark:text-emerald-400">+{formatRupees(income.amount)}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteIncome(income.id)}
                    className="text-red-600 hover:text-red-700"
                    title="Delete income"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Add Income</h3>
            <form onSubmit={handleCreateIncome} className="space-y-4">
              <input
                required
                className="input-field"
                placeholder="Title (e.g. Salary, Freelance)"
                value={formData.title}
                onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              />

              <input
                required
                type="number"
                min="0"
                step="0.01"
                className="input-field"
                placeholder="Amount"
                value={formData.amount}
                onChange={(e) => setFormData((prev) => ({ ...prev, amount: e.target.value }))}
              />

              <input
                className="input-field"
                placeholder="Category"
                value={formData.category}
                onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
              />

              <input
                className="input-field"
                placeholder="Source (optional)"
                value={formData.source}
                onChange={(e) => setFormData((prev) => ({ ...prev, source: e.target.value }))}
              />

              <select
                className="input-field"
                value={formData.account_id}
                onChange={(e) => setFormData((prev) => ({ ...prev, account_id: e.target.value }))}
              >
                <option value="">No account link</option>
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.name} ({formatRupees(acc.balance)})
                  </option>
                ))}
              </select>

              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={formData.is_recurring}
                  onChange={(e) => setFormData((prev) => ({ ...prev, is_recurring: e.target.checked }))}
                />
                Recurring income
              </label>

              {formData.is_recurring && (
                <select
                  className="input-field"
                  value={formData.recurring_frequency}
                  onChange={(e) => setFormData((prev) => ({ ...prev, recurring_frequency: e.target.value }))}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              )}

              <textarea
                className="input-field"
                rows={3}
                placeholder="Notes"
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              />

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1 inline-flex items-center justify-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default IncomesPage
